import { Link } from "wouter";
import { useSEO } from "@/hooks/use-seo";
import { PublicHeader } from "@/components/public-header";
import { PublicFooter } from "@/components/public-footer";
import { Button } from "@/components/ui/button";
import { partnerPortalApplyUrl } from "@/lib/partner-portal-url";
import {
  Award,
  CheckCircle2,
  ArrowRight,
  TrendingUp,
  Users,
  BarChart3,
  ShieldCheck,
  Mail,
  Building2,
  Zap,
} from "lucide-react";

const PAIN_POINTS = [
  {
    icon: BarChart3,
    title: "Things falling through the cracks",
    body: "At 250+ rooms across dozens of properties, maintenance requests get lost, vendor jobs go untracked, and issues that should take hours turn into week-long fires.",
  },
  {
    icon: TrendingUp,
    title: "No clear path to 1,000 rooms",
    body: "You've proven the model. But the manual processes that got you to 250 rooms are exactly what's blocking the next phase. Spreadsheets and scattered tools don't scale.",
  },
  {
    icon: Users,
    title: "Managing teams without visibility",
    body: "Coordinating vendors, tracking tasks, and keeping hosts aligned is a full-time job on its own — one that leaves no room for growth, strategy, or sleep.",
  },
];

const BENEFITS = [
  {
    icon: ShieldCheck,
    title: "Certified Property Manager badge",
    body: "Get recognized as a PadPulse CPM — a credential that signals operational credibility to hosts, investors, and the PadSplit ecosystem.",
  },
  {
    icon: Zap,
    title: "One platform for everything",
    body: "Properties, rooms, pricing, messaging, vendors, smart devices, and financials — all in one place, all talking to each other, all visible in real time.",
  },
  {
    icon: Building2,
    title: "Built for 250+ rooms and growing",
    body: "PadPulse's operations suite is designed for scale. Task automation, vendor CRM, supply tracking, and multi-property analytics give you the leverage you need to keep growing.",
  },
];

export default function CertifiedPropertyManagerPage() {
  useSEO({
    tabTitle: "Become a Certified Property Manager",
    title: "Become a Certified Property Manager",
    description: "Become a PadPulse Certified Property Manager and get listed for investors using PadPulse Research.",
    path: "/certified-property-manager",
    // Certified PM listing is hidden (backend D24): unlinked and noindexed.
    noindex: true,
  });
  const applyUrl = partnerPortalApplyUrl("property_manager");

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <PublicHeader />

      {/* Hero */}
      <section className="bg-brand-dark text-white py-20 md:py-28 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-brand-accent/20 border border-brand-accent/40 rounded-full px-4 py-1.5 mb-6">
            <Award className="h-4 w-4 text-brand-accent" />
            <span className="text-sm font-semibold text-brand-accent tracking-wide uppercase">Certified Property Manager Program</span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight mb-6" data-testid="heading-hero">
            Do you manage more than{" "}
            <span className="text-brand-accent">250 rooms</span>{" "}
            on PadSplit?
          </h1>
          <p className="text-xl md:text-2xl text-white/80 leading-relaxed mb-10 max-w-3xl mx-auto">
            You've built something serious. Now let's clear the path to your next phase of growth — from 250 rooms to 1,000 and beyond.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a
              href={applyUrl}
              data-testid="button-hero-apply-now"
            >
              <Button
                size="lg"
                className="bg-brand-accent hover:bg-brand-accent/90 text-white font-bold px-8 py-4 text-lg shadow-lg shadow-orange-500/25"
              >
                Apply Now
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </a>
            <a
              href="mailto:pm@padpulse.io"
              className="text-white/70 hover:text-white text-sm font-medium flex items-center gap-2 transition-colors"
              data-testid="link-hero-email"
            >
              <Mail className="h-4 w-4" />
              pm@padpulse.io
            </a>
          </div>
        </div>
      </section>

      {/* Pain Points */}
      <section className="py-20 px-6 bg-slate-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-testid="heading-pain-points">
              You've done the hard part. Now the cracks are showing.
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              At 250+ rooms, the limitations of duct-tape management become unavoidable. Sound familiar?
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {PAIN_POINTS.map((p) => (
              <div
                key={p.title}
                className="bg-white rounded-2xl border border-slate-200 p-7 shadow-sm"
                data-testid={`card-pain-${p.title.toLowerCase().replace(/\s+/g, "-").slice(0, 20)}`}
              >
                <div className="w-11 h-11 rounded-xl bg-red-100 flex items-center justify-center mb-5">
                  <p.icon className="h-5 w-5 text-red-600" />
                </div>
                <h3 className="font-bold text-slate-900 text-lg mb-2">{p.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The Invitation */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-brand/10 rounded-full px-4 py-1.5 mb-6">
                <CheckCircle2 className="h-4 w-4 text-brand" />
                <span className="text-sm font-semibold text-brand">The next phase</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6" data-testid="heading-invitation">
                Join PadPulse as a Certified Property Manager
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                PadPulse's Certified Property Manager program is designed for serious operators who are ready to trade chaos for clarity. We're opening the program to operators managing 250+ rooms on PadSplit who want a structured, tech-enabled path to their next thousand rooms.
              </p>
              <p className="text-slate-600 leading-relaxed mb-8">
                As a CPM, you get the tools, the visibility, and the infrastructure to operate like a professional — and scale like one.
              </p>
              <a href={applyUrl} data-testid="button-invite-apply-now">
                <Button
                  size="lg"
                  className="bg-brand hover:bg-brand/90 text-white font-bold"
                >
                  Apply Now
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </a>
            </div>
            <div className="space-y-4">
              {BENEFITS.map((b) => (
                <div
                  key={b.title}
                  className="flex items-start gap-4 bg-slate-50 rounded-xl p-5 border border-slate-100"
                  data-testid={`card-benefit-${b.title.toLowerCase().replace(/\s+/g, "-").slice(0, 20)}`}
                >
                  <div className="w-10 h-10 rounded-xl bg-brand/10 flex items-center justify-center shrink-0">
                    <b.icon className="h-5 w-5 text-brand" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900 text-sm mb-1">{b.title}</p>
                    <p className="text-slate-600 text-xs leading-relaxed">{b.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Eligibility */}
      <section className="py-16 px-6 bg-brand-dark">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-6" data-testid="heading-eligibility">
            Who qualifies?
          </h2>
          <div className="grid sm:grid-cols-3 gap-6 mb-10">
            {[
              { label: "250+ rooms", sub: "actively managed on PadSplit" },
              { label: "Proven operator", sub: "with a track record of results" },
              { label: "Ready to scale", sub: "toward 1,000 rooms and beyond" },
            ].map((item) => (
              <div key={item.label} className="bg-white/5 border border-white/10 rounded-xl p-5" data-testid={`card-eligibility-${item.label.replace(/\s+/g, "-").toLowerCase()}`}>
                <p className="text-brand-accent font-bold text-xl mb-1">{item.label}</p>
                <p className="text-white/60 text-sm">{item.sub}</p>
              </div>
            ))}
          </div>
          <p className="text-white/60 text-sm mb-8">
            Applications are reviewed within 2 business days. If approved, you'll receive a one-time password to sign the program agreement and activate your CPM portal.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a href={applyUrl} data-testid="button-eligibility-apply-now">
              <Button
                size="lg"
                className="bg-brand-accent hover:bg-brand-accent/90 text-white font-bold px-8"
              >
                Apply Now
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </a>
            <a
              href="mailto:pm@padpulse.io"
              className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm font-medium transition-colors"
              data-testid="link-eligibility-email"
            >
              <Mail className="h-4 w-4" />
              Email us at pm@padpulse.io if you have any further questions
            </a>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
